import { useState, useEffect, useRef } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { setError } from '../store/offersSlice';
import { api } from '../api/client';

const POLL_INTERVAL = 3000;

/**
 * Выгрузка обработанного предложения в Excel.
 * Файл собирается на бэке в фоне (одна из вьюх считается несколько минут),
 * поэтому стартуем задачу и опрашиваем статус. Когда файл готов — ждём клика:
 * window.open без действия пользователя браузер блокирует как всплывающее окно (особенно в iframe Б24).
 */
export function useReportExport(offerCode: number | null) {
    const dispatch = useDispatch<AppDispatch>();
    const [stage, setStage] = useState<string | null>(null);
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [readyId, setReadyId] = useState<string | null>(null);
    const [, setTick] = useState(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stop = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        setStage(null);
        setStartedAt(null);
    };

    // Смена предложения или уход со вкладки — перестаём опрашивать (файл на бэке всё равно соберётся)
    useEffect(() => () => { stop(); setReadyId(null); }, [offerCode]);

    // Тикаем раз в секунду, пока идёт выгрузка — для таймера на кнопке
    useEffect(() => {
        if (startedAt === null) return;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [startedAt]);

    const poll = (id: string) => {
        timerRef.current = setTimeout(async () => {
            try {
                const job = await api.getReportExport(id);
                if (job.status === 'done') {
                    stop();
                    setReadyId(id);
                } else if (job.status === 'error') {
                    stop();
                    dispatch(setError(job.error || 'Не удалось сформировать выгрузку'));
                } else {
                    setStage(job.stage);
                    poll(id);
                }
            } catch (e: any) {
                stop();
                dispatch(setError(e.message || 'Не удалось получить статус выгрузки'));
            }
        }, POLL_INTERVAL);
    };

    const start = async () => {
        if (!offerCode || stage) return;
        setReadyId(null);
        setStage('Запуск выгрузки');
        setStartedAt(Date.now());
        try {
            const job = await api.startReportExport(offerCode);
            setStage(job.stage);
            poll(job.id);
        } catch (e: any) {
            stop();
            dispatch(setError(e.message || 'Не удалось запустить выгрузку'));
        }
    };

    // Вызывается из клика — window.open не блокируется
    const download = () => {
        if (!readyId) return;
        const token = document.cookie.match(/b24_token=([^;]+)/)?.[1] || '';
        window.open(`/api/report-exports/${readyId}/download?_token=${encodeURIComponent(token)}`, '_blank');
        setReadyId(null);
    };

    const seconds = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    const elapsed = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

    return { start, download, inProgress: stage !== null, ready: readyId !== null, stage, elapsed };
}
